// Third Party
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';

// Components
import { Button } from '@capintel/component-lib/button';
import { InviteConfirmationModal, InvitePeopleModal, SwitchAccessModal } from '../modals';
import { BurgerMenuSection } from './HeaderBurgerMenuSection';

// Utils
import ZendeskUtils from '@/utils/3rd-party/zendesk';
import { NAV_PATHS } from '@/utils/constants';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '@/utils/permissions';
import { formatMessage } from '@/utils/i18n';
import { FormattedMessageV2 } from '../i18n';

// Redux Actions
import { inviteUsers, logOut } from '@/redux/modules/auth';
import { setSelectedDelegateAccess } from '@/redux/modules/delegates';

// Types
import NavigationItem from '@/types/navigation-item';
import User from '@/types/user';

type HeaderBurgerMenuProps = {
  inviteUsers: (emails: string[]) => Promise<void>;
  logOut: () => Promise<string | null>;
  menuItems: NavigationItem[];
  open: boolean;
  setOpen: (open: boolean) => void;
  setSelectedDelegateAccess: (id: string) => Promise<void>;
  user: User;
};

type ProfileItem = {
  label: string;
  value?: string;
  hidden: boolean;
  callback?: (val: boolean) => void;
};

const HeaderBurgerMenu: React.FC<HeaderBurgerMenuProps> = ({ menuItems, user, logOut, inviteUsers, setSelectedDelegateAccess, open, setOpen }) => {
  const [showSwitchAccessModal, setShowSwitchAccessModal] = useState(false);
  const [showInvitesModal, setShowInvitesModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [menuTop, setMenuTop] = useState(0);
  const history = useHistory();
  const { authUser } = user;

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = open ? 'hidden' : originalOverflow;

    if (!open) return;

    const updateMenuTop = () => {
      const header = document.getElementById('main-header');
      if (header) {
        const rect = header.getBoundingClientRect();
        setMenuTop(rect.bottom);
      }
    };

    updateMenuTop();
    window.addEventListener('resize', updateMenuTop);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('resize', updateMenuTop);
    };
  }, [open]);

  const profileItem: ProfileItem[] = [
    { label: formatMessage('SiteNavigation.Settings'), value: NAV_PATHS.SETTINGS, hidden: !userHasPermissionEnabled(user, PERMISSIONS.SECTION_SETTINGS) },
    { label: formatMessage('SiteNavigation.InvitePeople'), callback: setShowInvitesModal, hidden: !userHasPermission(user, PERMISSIONS.INVITE_PEOPLE) },
    {
      label: formatMessage('SiteNavigation.Announcements'),
      value: NAV_PATHS.ANNOUNCEMENTS,
      hidden: !userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_NOTIFICATION_CENTER),
    },
    {
      label: formatMessage('Delegates.SwitchAccount'),
      callback: setShowSwitchAccessModal,
      hidden: authUser.delegateAccess?.myAccesses == null || authUser.delegateAccess?.myAccesses.length === 0,
    },
  ];

  const handleNavClick = (item: NavigationItem) => {
    setOpen(false);
    if (item.value) {
      history.push(item.value);
    }
  };

  const handleProfileClick = (item: ProfileItem) => {
    setOpen(false);
    if (item.callback) {
      item.callback(true);
    } else if (item.value) {
      history.push(item.value);
    }
  };

  const handleLogout = () => {
    logOut().then(path => {
      ZendeskUtils.logout();
      if (path) {
        window.location.href = path;
      } else {
        history.replace('/auth/login');
      }
    });
  };

  const switchAccess = (id: string) => {
    setShowSwitchAccessModal(false);

    if (!user || id === user.id) {
      return;
    }

    setSelectedDelegateAccess(id).then(() => {
      window.location.replace(NAV_PATHS.COMPARISONS);
    });
  };
  const reOpenInvites = () => {
    setShowConfirmationModal(false);
    setShowInvitesModal(true);
  };

  const inviteSuccess = () => {
    setShowConfirmationModal(true);
    setShowInvitesModal(false);
  };

  return (
    <>
      <SwitchAccessModal show={showSwitchAccessModal} onCancel={() => setShowSwitchAccessModal(false)} onConfirm={switchAccess} user={user} />
      <InvitePeopleModal onSuccess={inviteSuccess} showModal={showInvitesModal} onSubmit={inviteUsers} onCancel={() => setShowInvitesModal(false)} />
      <InviteConfirmationModal showModal={showConfirmationModal} onConfirm={() => reOpenInvites()} onCancel={() => setShowConfirmationModal(false)} />

      <div className="burger-menu-trigger-container" data-testid="burger-menu">
        {open && (
          <div
            className="burger-menu-card burger-menu-content"
            style={{
              top: menuTop,
              maxHeight: `calc(100vh - ${menuTop}px)`,
            }}
          >
            <div className="burger-menu-sections">
              <BurgerMenuSection
                title={formatMessage('SiteNavigation.BurgerMenu.Navigation')}
                items={menuItems.map(item => ({
                  label: item.label as string,
                  onClick: () => handleNavClick(item),
                }))}
              />

              <BurgerMenuSection
                title={formatMessage('SiteNavigation.BurgerMenu.Profile')}
                items={profileItem
                  .filter(item => !item.hidden)
                  .map(item => ({
                    label: item.label,
                    onClick: () => handleProfileClick(item),
                  }))}
              />
            </div>

            <div className="burger-menu-logout-btn-container">
              <Button variant="primary" onClick={handleLogout}>
                <FormattedMessageV2 toLocalize="SiteNavigation.LogOut" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default connect(null, { logOut, inviteUsers, setSelectedDelegateAccess })(HeaderBurgerMenu);
